import { useEffect, useId, useState } from 'react';
import { useTheme } from '../lib/theme';

interface Props {
  source: string;
}

// Register the ELK layout loader once per page lifetime. ELK gives strict
// per-rank layering for diagrams that dagre's default cycle-breaking
// would otherwise render sideways (e.g. value loops where actor->contract
// and contract->actor edges balance out).
let elkRegistered = false;

/**
 * Lazy-loaded Mermaid diagram. Mermaid + ELK are dynamic-imported on first
 * render so pages without diagrams pay no cost. Re-renders on theme switch
 * so the SVG colors stay coherent with light/dark.
 *
 * Authors write ```mermaid blocks in MDX and mdxComponents.tsx routes them
 * to this component via the `pre` override. Diagrams can opt into ELK
 * layout via YAML frontmatter inside the fenced block.
 */
export default function Mermaid({ source }: Props) {
  const { theme } = useTheme();
  // useId produces ':r0:' style identifiers that aren't valid in SVG IDs.
  const safeId = useId().replace(/[:]/g, '');
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setSvg(null);
    (async () => {
      try {
        const mermaidMod = await import('mermaid');
        const mermaid = mermaidMod.default;

        if (!elkRegistered) {
          const elkMod = await import('@mermaid-js/layout-elk');
          // Mermaid 11+ accepts loader exports; the module's default
          // export is the loader factory.
          (mermaid as unknown as {
            registerLayoutLoaders: (l: unknown) => void;
          }).registerLayoutLoaders(elkMod.default);
          elkRegistered = true;
        }

        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          fontFamily: 'Inter, system-ui, sans-serif',
          themeVariables: theme === 'dark'
            ? {
                background:           '#0e0f10',
                primaryColor:         '#15171a',
                primaryTextColor:     '#f1f3f5',
                primaryBorderColor:   '#34d4bf',
                lineColor:            '#9ca3af',
                secondaryColor:       '#1a1c1e',
                tertiaryColor:        '#181a1d',
                clusterBkg:           '#15171a',
                clusterBorder:        '#25272b',
                titleColor:           '#9ca3af',
                edgeLabelBackground:  '#0e0f10',
              }
            : {
                background:           '#ffffff',
                primaryColor:         '#fafafa',
                primaryTextColor:     '#111418',
                primaryBorderColor:   '#0a5e54',
                lineColor:            '#6b7280',
                secondaryColor:       '#f5f5f5',
                tertiaryColor:        '#fafafa',
                clusterBkg:           '#fafafa',
                clusterBorder:        '#e5e7eb',
                titleColor:           '#6b7280',
                edgeLabelBackground:  '#ffffff',
              },
        });
        const renderId = `mermaid-${safeId}-${Date.now().toString(36)}`;
        const { svg: rendered } = await mermaid.render(renderId, source);
        if (!cancelled) setSvg(rendered);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [source, theme, safeId]);

  if (error) {
    return (
      <div className="mermaid-error" role="alert">
        <strong>Diagram error:</strong> {error}
        <pre>{source}</pre>
      </div>
    );
  }
  if (!svg) {
    return <div className="mermaid-loading">Loading diagram…</div>;
  }
  return (
    <div
      className="mermaid-diagram"
      data-pagefind-ignore
      // SVG is sanitized by mermaid (securityLevel: 'strict').
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
